/**
 * Generic CSV helpers for the Market + Signals export buttons.
 *
 * The backend endpoints stream pre-formatted CSV (text/csv content type)
 * so the frontend's job is just to trigger the browser download. We use
 * fetch + Blob rather than `<a download>` so the existing API-client
 * auth (x-api-key header) is preserved.
 */

import { getApiKey, getBaseUrl } from '@/api/client';

/** Trigger a browser download of a CSV blob already in memory. */
export function downloadCsvBlob(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Fetch a CSV from an admin endpoint (preserves x-api-key auth) and
 *  trigger the browser download. Filename is derived from the path if
 *  not supplied; callers can override. */
export async function downloadCsvFromApi(path: string, filename?: string): Promise<void> {
  const key = getApiKey();
  const res = await fetch(`${getBaseUrl()}${path}`, {
    headers: key ? { 'x-api-key': key } : {},
  });
  if (!res.ok) throw new Error(`csv download failed: ${res.status}`);
  const text = await res.text();
  // Fallback filename from path's last segment.
  const fname = filename ?? (path.split('?')[0].split('/').pop() || 'export') + '.csv';
  downloadCsvBlob(fname, text);
}
